import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DownloadOutlined,
  PrinterOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_COLOR = {
  AVAILABLE: 'green',
  ASSIGNED: 'blue',
  BLOCKED: 'orange',
  DEACTIVATED: 'red',
};

const qrImg = (payload, size = 160) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    payload || '',
  )}`;

const downloadQrPng = async (item, size = 512) => {
  const serial = item?.qrSerial || 'qr';
  const url = qrImg(item?.qrPayload, size);
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${serial}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (_) {
    // Fallback if CORS blocks blob download
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = `${serial}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return false;
  }
};

const QrManagementPage = () => {
  const colors = useBrandToken();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: undefined, serial: '' });
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [qty, setQty] = useState(10);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [reassign, setReassign] = useState(null);
  const [printItems, setPrintItems] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleDownloadOne = async (item) => {
    if (!item?.qrPayload) {
      message.warning('QR payload missing');
      return;
    }
    const key = item.id || item.qrSerial;
    setDownloadingId(key);
    try {
      await downloadQrPng(item);
      message.success(`Downloaded ${item.qrSerial || 'QR'}`);
    } catch (_) {
      message.error('Could not download QR');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadMany = async (items) => {
    const list = (items || []).filter((i) => i?.qrPayload);
    if (!list.length) {
      message.warning('No QRs to download');
      return;
    }
    setDownloadingAll(true);
    try {
      for (let i = 0; i < list.length; i += 1) {
        // Small delay so browsers don't block multiple downloads
        // eslint-disable-next-line no-await-in-loop
        await downloadQrPng(list[i]);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250));
      }
      message.success(`Downloaded ${list.length} QR image(s)`);
    } catch (_) {
      message.error('Some QR downloads failed');
    } finally {
      setDownloadingAll(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/admin/qr/stats');
      setStats(res?.data || res);
    } catch (_) {
      setStats(null);
    }
  };

  const fetchRows = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        page: nextPage,
        limit: 20,
        ...(nextFilters.status ? { status: nextFilters.status } : {}),
        ...(nextFilters.serial ? { serial: nextFilters.serial } : {}),
        ...(nextFilters.merchantId ? { merchantId: nextFilters.merchantId } : {}),
        ...(nextFilters.from ? { from: nextFilters.from } : {}),
        ...(nextFilters.to ? { to: nextFilters.to } : {}),
      };
      const res = await axios.get('/admin/qr', { params });
      setRows(res?.data || []);
      setTotal(res?.total || 0);
      setPage(nextPage);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRows(1, filters);
    axios
      .get('/admin/merchants')
      .then((res) => setMerchants(Array.isArray(res?.data) ? res.data : res || []))
      .catch(() => setMerchants([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (record) => {
    try {
      const [one, hist] = await Promise.all([
        axios.get(`/admin/qr/${record.id}`),
        axios.get(`/admin/qr/${record.id}/history`),
      ]);
      setDetail(one?.data || record);
      setHistory(hist?.data || null);
    } catch (_) {
      setDetail(record);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post('/admin/qr/generate', { quantity: qty });
      message.success(res?.message || `Generated ${qty} QR codes`);
      setGenerateOpen(false);
      fetchStats();
      fetchRows(1, filters);
      if (res?.data?.items?.length) {
        setPrintItems(res.data.items);
      }
    } finally {
      setGenerating(false);
    }
  };

  const runStatus = async (id, path, extra = {}) => {
    await axios.post(`/admin/qr/${id}/${path}`, extra);
    message.success('Updated');
    setDetail(null);
    fetchStats();
    fetchRows();
  };

  const printPack = (items) => {
    const list = items || printItems || [];
    const html = `<!doctype html><html><head><title>QR print pack</title>
      <style>
        body { font-family: sans-serif; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .card { border: 1px solid #ddd; padding: 12px; text-align: center; page-break-inside: avoid; }
        img { width: 160px; height: 160px; }
        .serial { font-weight: 700; margin-top: 8px; letter-spacing: 0.04em; }
      </style></head><body>
      <h2>ScanU QR inventory</h2>
      <div class="grid">
        ${list
          .map(
            (i) => `<div class="card">
              <img src="${qrImg(i.qrPayload, 220)}" alt="${i.qrSerial}" />
              <div class="serial">${i.qrSerial}</div>
            </div>`,
          )
          .join('')}
      </div>
      <script>window.onload = () => window.print();</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token') || '';
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.serial) params.set('serial', filters.serial);
    window.open(
      `${axios.defaults.baseURL}/admin/qr/export?${params.toString()}`,
      '_blank',
    );
    // Fallback download via axios blob if token needed
    axios
      .get('/admin/qr/export', { params: filters, responseType: 'blob' })
      .then((blob) => {
        const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'qr-inventory.csv';
        a.click();
      })
      .catch(() => {});
    void token;
  };

  const columns = useMemo(
    () => [
      { title: 'Serial', dataIndex: 'qrSerial', key: 'qrSerial' },
      {
        title: 'Status',
        dataIndex: 'status',
        render: (s) => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>,
      },
      {
        title: 'Merchant',
        render: (_, r) => r.assignedMerchant?.full_name || '—',
      },
      {
        title: 'Salesman',
        render: (_, r) => r.assignedSalesman?.full_name || '—',
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        render: (v) => (v ? new Date(v).toLocaleString() : '—'),
      },
      {
        title: '',
        key: 'act',
        width: 180,
        render: (_, r) => (
          <Space size={0}>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              loading={downloadingId === (r.id || r.qrSerial)}
              onClick={() => handleDownloadOne(r)}
            >
              Download
            </Button>
            <Button type="link" onClick={() => openDetail(r)}>
              Details
            </Button>
          </Space>
        ),
      },
    ],
    [downloadingId],
  );

  return (
    <PageShell
      title="QR Management"
      description="Pre-generate opaque store QR stickers, then assign them to merchants. This inventory does not process payments."
      actions={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchRows(); }}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>
            Export CSV
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate QR
          </Button>
        </Space>
      }
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          ['Total', stats?.total],
          ['Available', stats?.available],
          ['Assigned', stats?.assigned],
          ['Blocked', stats?.blocked],
          ['Deactivated', stats?.deactivated],
          ['Assigned today', stats?.assignmentsToday],
        ].map(([title, value]) => (
          <Col xs={12} sm={8} lg={4} key={title}>
            <Card size="small">
              <Statistic title={title} value={value ?? 0} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            allowClear
            placeholder="Serial"
            style={{ width: 180 }}
            value={filters.serial}
            onChange={(e) => setFilters((f) => ({ ...f, serial: e.target.value }))}
            onPressEnter={() => fetchRows(1, filters)}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={filters.status}
            onChange={(status) => {
              const next = { ...filters, status };
              setFilters(next);
              fetchRows(1, next);
            }}
            options={['AVAILABLE', 'ASSIGNED', 'BLOCKED', 'DEACTIVATED'].map((s) => ({
              value: s,
              label: s,
            }))}
          />
          <RangePicker
            onChange={(range) => {
              const next = {
                ...filters,
                from: range?.[0]?.toISOString(),
                to: range?.[1]?.toISOString(),
              };
              setFilters(next);
              fetchRows(1, next);
            }}
          />
          <Button type="primary" onClick={() => fetchRows(1, filters)}>
            Search
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: (p) => fetchRows(p, filters),
        }}
      />

      <Modal
        title="Generate QR inventory"
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={generate}
        confirmLoading={generating}
        okText="Generate"
      >
        <p>Creates unique, non-guessable serials and tokens. Quantity 1–1000.</p>
        <InputNumber min={1} max={1000} value={qty} onChange={setQty} style={{ width: '100%' }} />
      </Modal>

      <Drawer
        width={520}
        title={detail ? `QR ${detail.qrSerial}` : 'QR'}
        open={!!detail}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={qrImg(detail.qrPayload, 200)} alt={detail.qrSerial} />
              <div style={{ fontWeight: 700, marginTop: 8 }}>{detail.qrSerial}</div>
              <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag>
            </div>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Payload">{detail.qrPayload}</Descriptions.Item>
              <Descriptions.Item label="Merchant">
                {detail.assignedMerchant?.full_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Outlet">
                {detail.assignedOutlet?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Salesman">
                {detail.assignedSalesman?.full_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Assigned">
                {detail.assignedAt ? new Date(detail.assignedAt).toLocaleString() : '—'}
              </Descriptions.Item>
            </Descriptions>
            <Space wrap style={{ marginTop: 16 }}>
              <Button
                icon={<DownloadOutlined />}
                loading={downloadingId === (detail.id || detail.qrSerial)}
                onClick={() => handleDownloadOne(detail)}
              >
                Download PNG
              </Button>
              <Button icon={<PrinterOutlined />} onClick={() => printPack([detail])}>
                Print
              </Button>
              {detail.status === 'ASSIGNED' && (
                <>
                  <Popconfirm
                    title="Unassign this QR? History is kept."
                    onConfirm={() => runStatus(detail.id, 'unassign')}
                  >
                    <Button>Unassign</Button>
                  </Popconfirm>
                  <Button onClick={() => setReassign(detail)}>Reassign</Button>
                </>
              )}
              {detail.status !== 'BLOCKED' && detail.status !== 'DEACTIVATED' && (
                <Popconfirm
                  title="Block this QR?"
                  onConfirm={() => runStatus(detail.id, 'block')}
                >
                  <Button danger>Block</Button>
                </Popconfirm>
              )}
              {detail.status !== 'DEACTIVATED' && (
                <Popconfirm
                  title="Deactivate permanently? Cannot assign afterwards."
                  onConfirm={() => runStatus(detail.id, 'deactivate')}
                >
                  <Button danger>Deactivate</Button>
                </Popconfirm>
              )}
            </Space>
            <Typography.Title level={5} style={{ marginTop: 24 }}>
              Assignment history
            </Typography.Title>
            <Timeline
              items={(history?.assignments || []).map((a) => ({
                children: (
                  <div>
                    <Text strong>
                      {a.status === 'ACTIVE' ? 'Assigned' : 'Unassigned'}
                    </Text>
                    <div>
                      {(a.merchantId?.full_name || a.merchantId) ?? '—'} ·{' '}
                      {a.salesmanId?.full_name || '—'}
                    </div>
                    <Text type="secondary">
                      {a.assignedAt ? new Date(a.assignedAt).toLocaleString() : ''}
                      {a.unassignedAt
                        ? ` → ${new Date(a.unassignedAt).toLocaleString()}`
                        : ''}
                    </Text>
                  </div>
                ),
              }))}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Reassign QR"
        open={!!reassign}
        onCancel={() => setReassign(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={async (values) => {
            await axios.post(`/admin/qr/${reassign.id}/reassign`, values);
            message.success('Reassigned');
            setReassign(null);
            setDetail(null);
            fetchStats();
            fetchRows();
          }}
        >
          <Form.Item
            name="merchantId"
            label="New merchant"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={(Array.isArray(merchants) ? merchants : []).map((m) => ({
                value: m._id,
                label: `${m.full_name || m.username || m._id}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="outletId" label="Outlet ID (required if merchant has several)">
            <Input />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Confirm reassignment
          </Button>
        </Form>
      </Modal>

      {printItems && (
        <Modal
          title="Generated QRs"
          open={!!printItems}
          onCancel={() => setPrintItems(null)}
          width={720}
          footer={[
            <Button key="close" onClick={() => setPrintItems(null)}>
              Close
            </Button>,
            <Button
              key="download-all"
              icon={<DownloadOutlined />}
              loading={downloadingAll}
              onClick={() => handleDownloadMany(printItems)}
            >
              Download all PNGs
            </Button>,
            <Button
              key="print"
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => printPack(printItems)}
            >
              Print / Save PDF
            </Button>,
          ]}
        >
          <p style={{ marginBottom: 12 }}>
            {printItems.length} codes in batch {printItems[0]?.batchId || ''}.
            Download individual PNGs below or all at once.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 12,
              maxHeight: 420,
              overflowY: 'auto',
            }}
          >
            {printItems.map((item) => (
              <Card
                key={item.id || item.qrSerial}
                size="small"
                styles={{ body: { padding: 10, textAlign: 'center' } }}
              >
                <img
                  src={qrImg(item.qrPayload, 140)}
                  alt={item.qrSerial}
                  style={{ width: 110, height: 110 }}
                />
                <div style={{ fontWeight: 700, fontSize: 12, marginTop: 6 }}>
                  {item.qrSerial}
                </div>
                <Button
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  loading={downloadingId === (item.id || item.qrSerial)}
                  onClick={() => handleDownloadOne(item)}
                  style={{ paddingInline: 0 }}
                >
                  Download
                </Button>
              </Card>
            ))}
          </div>
        </Modal>
      )}
    </PageShell>
  );
};

export default QrManagementPage;
